import api from './client';
import { Project, Schedule, Activity } from '../types';
import { API_BASE_URL } from '../config';
import { getAuthHeaders } from './workosAuth';

// Use the correct API URL - ensure it points to the backend
const API_URL = API_BASE_URL;

export const uploadSchedule = async (projectId: string, file: File): Promise<Schedule> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('project_id', projectId);

  const response = await api.post(`${API_URL}/schedules/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getProjectSchedules = async (projectId: string): Promise<Schedule[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/projects/${projectId}/schedules`, {
      credentials: 'include',
      headers,
    });
    if (!response.ok) {
      throw new Error('Failed to fetch schedules');
    }
    const responseData = await response.json();
    
    // Handle both response formats - with or without data wrapper
    if (responseData.data) {
      return responseData.data;
    } else if (Array.isArray(responseData)) {
      return responseData;
    } else {
      console.warn('Unexpected response format from schedules API:', responseData);
      return [];
    }
  } catch (error) {
    console.error('Error fetching project schedules:', error);
    return []; // Return empty array instead of throwing, to avoid errors in the UI
  }
};

export const getScheduleDetails = async (scheduleId: string): Promise<Schedule> => {
  try {
    // First try the new endpoint format
    try {
      const response = await api.get(`${API_URL}/xer/dashboard`);
      console.log('Using new xer/dashboard endpoint for schedule details');
      // Handle both response formats - with or without data wrapper
      if (response.data.data) {
        return response.data.data;
      } else {
        return response.data;
      }
    } catch (e) {
      // If the new endpoint fails, fall back to the old one
      console.log('Falling back to old projects/schedules endpoint');
      const response = await api.get(`${API_URL}/projects/schedules/${scheduleId}`);
      if (response.data.data) {
        return response.data.data;
      } else {
        return response.data;
      }
    }
  } catch (error) {
    console.error('Error fetching schedule details:', error);
    throw error;
  }
};

export const getProjectDetails = async (projectId: number): Promise<Project> => {
  const response = await api.get(`${API_URL}/projects/${projectId}`);
  return response.data;
};

export const parseXerFile = async (file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(`${API_URL}/xer/working-upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteSchedule = async (scheduleId: string): Promise<Schedule> => {
  const response = await api.delete(`${API_URL}/schedules/${scheduleId}`);
  return response.data;
};

export const renameSchedule = async (scheduleId: string, newName: string): Promise<Schedule> => {
  const response = await api.patch(`${API_URL}/projects/schedules/${scheduleId}/rename`, null, {
    params: { new_name: newName },
  });
  return response.data;
};

export const getScheduleLookahead = async (scheduleId: string, days: number = 14): Promise<any[]> => {
  const response = await api.get(`${API_URL}/schedules/${scheduleId}/lookahead`, {
    params: { days },
  });
  return response.data;
};

export const downloadXer = async (scheduleId: string, filename: string): Promise<void> => {
  const response = await api.get(`${API_URL}/schedules/${scheduleId}/export-xer`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadExcel = async (scheduleId: string, filename: string): Promise<void> => {
  const response = await api.get(`${API_URL}/v1/schedule/export-excel/${scheduleId}`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const updateActivity = async (activityId: string, update: Partial<Activity>): Promise<Activity> => {
  const response = await api.patch(`${API_URL}/v1/schedule/activities/${activityId}`, update);
  return response.data;
};

export const updateScheduleStatus = async (scheduleId: string, status: string): Promise<Schedule> => {
  try {
    const response = await api.put(`${API_URL}/schedules/${scheduleId}/status`, {
      status: status
    });
    
    if (response.data.status === 'success') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to update schedule status');
    }
  } catch (error) {
    console.error('Error updating schedule status:', error);
    throw error;
  }
};

export const getScheduleActivities = async (scheduleId: string): Promise<any[]> => {
  try {
    const response = await api.get(`${API_URL}/schedules/${scheduleId}/activities`);
    
    if (response.data.status === 'success') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to fetch schedule activities');
    }
  } catch (error) {
    console.error('Error fetching schedule activities:', error);
    throw error;
  }
};

export const getXERProjectTasks = async (projectId: string): Promise<{activities: any[], udf_labels: Record<string, string>} | any[]> => {
  try {
    const response = await api.get(`${API_URL}/activities/projects/${projectId}/activities`);
    // New format returns {activities, udf_labels}, old format was just an array
    return response.data;
  } catch (error) {
    console.error('Error fetching XER project tasks:', error);
    throw error;
  }
};

export const getXERProjectRelationships = async (projectId: string): Promise<any[]> => {
  try {
    const response = await api.get(`${API_URL}/activities/projects/${projectId}/relationships`);
    return response.data;
  } catch (error) {
    console.error('Error fetching XER project relationships:', error);
    throw error;
  }
};

export const getAllProjectWBS = async (projectId: string): Promise<any[]> => {
  try {
    const response = await api.get(`${API_URL}/activities/projects/${projectId}/wbs/all`);
    return response.data;
  } catch (error) {
    console.error('Error fetching all project WBS nodes:', error);
    throw error;
  }
};

export const getScheduleRelationships = async (scheduleId: string): Promise<any[]> => {
  try {
    const response = await api.get(`${API_URL}/schedules/${scheduleId}/relationships`);
    
    if (response.data.status === 'success') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to fetch schedule relationships');
    }
  } catch (error) {
    console.error('Error fetching schedule relationships:', error);
    throw error;
  }
};

export const compareSchedules = async (scheduleIds: string[]): Promise<any> => {
  try {
    console.log('compareSchedules called with:', scheduleIds);
    const requestBody = { schedule_ids: scheduleIds };
    console.log('Request body:', requestBody);
    
    const response = await api.post(`${API_URL}/schedules/compare`, requestBody);
    
    console.log('Response status:', response.status);
    console.log('Response data:', response.data);
    
    if (response.data.status === 'success') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to compare schedules');
    }
  } catch (error: any) {
    console.error('Error comparing schedules:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Response detail:', error.response.data?.detail);
    }
    
    // Fallback mock data for testing when backend is not available
    console.log('Using fallback mock comparison data');
    return {
      schedules: [
        {
          id: "adc9d388-4fac-4197-9258-8fd62a46d96f",
          name: "Schedule from 4418FP-23-24-BL6-EOT24RF-12A (R0) V20.12.xer",
          status: "ready",
          start_date: "2020-12-02 00:00:00",
          end_date: "2025-07-20T00:00:00",
          duration: 1691,
          activity_count: 1814,
          created_at: "2025-06-26T23:24:54.121390"
        },
        {
          id: "79a7daf7-2e47-43b3-83af-12f6785f0260",
          name: "Schedule from U22025-1300 250405.xer",
          status: "ready",
          start_date: "2025-01-29 00:00:00",
          end_date: "2025-05-27T00:00:00",
          duration: 118,
          activity_count: 3045,
          created_at: "2025-06-26T23:26:48.317126"
        }
      ],
      comparison_data: {
        total_schedules: 2,
        date_range: {
          earliest_start: "2020-12-02 00:00:00",
          latest_end: "2025-07-20T00:00:00"
        },
        activity_count_range: {
          min: 1814,
          max: 3045,
          total: 4859
        },
        duration_range: {
          min: 118,
          max: 1691,
          average: 904.5
        }
      },
      differences: [
        {
          schedule1_id: "adc9d388-4fac-4197-9258-8fd62a46d96f",
          schedule1_name: "Schedule from 4418FP-23-24-BL6-EOT24RF-12A (R0) V20.12.xer",
          schedule2_id: "79a7daf7-2e47-43b3-83af-12f6785f0260",
          schedule2_name: "Schedule from U22025-1300 250405.xer",
          start_date_difference_days: 1573,
          end_date_difference_days: -54,
          duration_difference_days: -1573,
          activity_count_difference: 1231
        }
      ]
    };
  }
}; 